"use client";
import styles from "./index.module.css";
import React, { PropsWithChildren, ReactNode, ForwardedRef } from "react";
import { useMediaQuery } from "@mantine/hooks";
import { theme } from "@/theme";
import { ScrollArea } from "@mantine/core";
import { MobileView } from "@/components/AppShell/MobileView";
import { DesktopView } from "@/components/AppShell/DesktopView";
import { LoadingView } from "@/components/AppShell/LoadingView";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import IconBackground from "@/components/IconBackground";
import { Loader } from "@/components/Loader";

interface AppShellProps {
  backButton?: ReactNode;
  dropdown?: ReactNode;
  isLoading?: boolean;
  loader?: ReactNode;
  size?: "lg" | "xl";
  scrollAreaRef?: ForwardedRef<HTMLDivElement>;
  contentStyles?: string;
  hintMessage: React.ReactElement | string;
  withFooter?: boolean;
  bottomContent?: ReactNode;
  footerRef?: ForwardedRef<HTMLDivElement>;
  withSaveContract?: boolean;
  fullScreen?: boolean;
  withInfoBanner?: boolean;
  noAssignedBonus?: boolean;
  isThankYouPage?: boolean;
}

const defaultLoader = (
  <IconBackground width={88} height={88}>
    <Loader width={40} height={40} />
  </IconBackground>
);

const AppShell = ({
  backButton,
  dropdown,
  isLoading = false,
  loader = defaultLoader,
  scrollAreaRef,
  size = "lg",
  contentStyles,
  hintMessage,
  withFooter = true,
  bottomContent,
  footerRef,
  children,
  withSaveContract = false,
  fullScreen = false,
  withInfoBanner = false,
  noAssignedBonus = false,
  isThankYouPage = false,
}: AppShellProps & PropsWithChildren) => {
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints?.xl})`) ?? true;

  return (
    <div className={styles.root}>
      <header>
        <Header
          withSaveContract={withSaveContract}
          dropdown={dropdown}
          withInfoBanner={withInfoBanner}
          noAssignedBonus={noAssignedBonus}
        />
      </header>
      {isLoading ? (
        <ScrollArea type="auto" key="loading" scrollbars="y" classNames={{ viewport: styles.viewport }}>
          <div className={styles.loadingView}>
            <LoadingView loader={loader} />
            {bottomContent}
            {withFooter && <Footer footerRef={footerRef} />}
          </div>
        </ScrollArea>
      ) : fullScreen ? (
        <div className={styles.fullScreen}>{children}</div>
      ) : (
        <ScrollArea type="auto" viewportRef={scrollAreaRef} scrollbars="y" classNames={{ viewport: styles.viewport }}>
          <div className={`${styles.main} ${contentStyles}`} data-bottom-content={!!bottomContent}>
            {isDesktop ? (
              <DesktopView hintMessage={hintMessage} size={size} backButton={backButton} isThankYouPage={isThankYouPage}>
                {children}
              </DesktopView>
            ) : (
              <MobileView
                hintMessage={hintMessage}
                size={size}
                backButton={backButton}
                dropdown={dropdown}
                isThankYouPage={isThankYouPage}>
                {children}
              </MobileView>
            )}
          </div>
          {bottomContent}
          {withFooter && <Footer footerRef={footerRef} />}
        </ScrollArea>
      )}
    </div>
  );
};

export default AppShell;
